import { isDefined } from "@clipboard-health/util-ts";
import axios from "axios";
import { z } from "zod";

import { useGetQuery, type UseGetQueryOptions } from "../../api";
import { environmentConfig } from "../../environment";
import { APP_V2_APP_EVENTS } from "../../lib";

const agentProfilePictureResponseSchema = z.object({
  distribution_url: z.string(),
});

export type ProfilePictureSize = "small" | "medium" | "large";

const NON_REPORTABLE_PROFILE_PICTURE_ERROR_STATUSES = new Set([400, 404]);
const SKIP_RETRY_PROFILE_PICTURE_ERROR_STATUSES = new Set([400, 404]);

interface GetAgentProfilePictureParams {
  agentId: string;
  size?: ProfilePictureSize;
}
export type AgentProfilePictureResponse = z.infer<typeof agentProfilePictureResponseSchema>;

export function useAgentProfilePicture(
  params: GetAgentProfilePictureParams,
  options: UseGetQueryOptions<AgentProfilePictureResponse> = {}
) {
  const { agentId, size } = params;
  const { enabled = true, ...queryOptions } = options;

  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/agentProfile/${agentId}/profile-image-distribution-url`,
    queryParams: size ? { size } : undefined,
    responseSchema: agentProfilePictureResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_AGENT_PROFILE_PICTURE_FAILURE,
      shouldLogError: shouldLogAgentProfilePictureError,
    },
    // It's ok to have a 404 here, however, if we don't set this to false,
    // this hook will throw and cause a global 404 error
    useErrorBoundary: false,
    retry: (failureCount, error) => failureCount < 3 && shouldRetryAgentProfilePictureError(error),
    ...queryOptions,
    enabled: enabled && agentId.trim().length > 0,
  });
}

function shouldLogAgentProfilePictureError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return true;
  }

  const status = error.response?.status;
  if (!isDefined(status)) {
    return true;
  }

  return !NON_REPORTABLE_PROFILE_PICTURE_ERROR_STATUSES.has(status);
}

function shouldRetryAgentProfilePictureError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return true;
  }

  const status = error.response?.status;
  if (!isDefined(status)) {
    return true;
  }

  return !SKIP_RETRY_PROFILE_PICTURE_ERROR_STATUSES.has(status);
}
